<template>
  <div ref="map" class="retailers-map-section">
    <loading-icon v-if="isGeoMapLoading"/>
    <p v-else-if="errorMessage" class="error-label text-center">{{errorMessage}}</p>
    <svg v-else class="line-graph-svg"
          :height="tallerHeight"
          :width="width">
      <g v-if="mapData && mapPath"
          class="region-paths"
          :transform="`translate(0, ${margin / 2})`">
        <path v-for="state in mapData" :key="state.id" :d="mapPath(state)"></path>
      </g>
      <g v-if="geoPoints"
          class="coordinates-paths"
          :transform="`translate(0, ${margin / 2})`">
        <circle v-for="(point, index) in geoPoints"
                :class="{selected: isLocationSelected(point.merchant_id)}"
                :key="index"
                :cx="point.long"
                :cy="point.lat" r="4"
                @click="selectLocation(point.merchant_id)"></circle>
      </g>
    </svg>
  </div>
</template>

<script>
  import * as d3 from 'd3'
  //https://s3-us-west-2.amazonaws.com/s.cdpn.io/39255/us-states.json -- where geoData came from
  import LoadingIcon from '../Shared/LoadingIcon.vue'
  import usGeoData from './usGeoJSONdata.json'
  import { advertiserAPI } from '../../services/api/advertiserAPI'

  export default {
    props: {
      currentAdvertiserId: [Number, String],
      selectedRetailerIds: Array
    },

    name: 'RetailersMap',

    components: { LoadingIcon },

    data() {
      return {
        errorMessage: null,
        geoPoints: null,
        height: null,
        margin: 20,
        mapData: null,
        mapPath: null,
        projection: null,
        retailerCoordinates: null,
        retailerRegions: null,
        usStates: null,
        width: null,
        isGeoMapLoading: true
      }
    },

    methods: {
      buildGeoPoints() {
        let geoPoints = null
        if (this.retailerCoordinates && !!this.retailerCoordinates.length && this.projection) {
          geoPoints = []
          this.retailerCoordinates.forEach((coords) => {
            const coordPositions = this.projection([coords.longitude, coords.latitude])
            if (coordPositions) {
              geoPoints.push({
                long: coordPositions[0],
                lat: coordPositions[1],
                merchant_id: coords.id
              })
            }
          })
        }
        this.geoPoints = geoPoints
        this.isGeoMapLoading = false
      },

      findSingleRegion(region) {
        const states = this.usStates
        return states.find( ({ properties }) => properties.name.toLowerCase() === region.toLowerCase() )
      },

      async loadRetailersGeoData() {
        let errorText = 'there was an error loading the geo data'
        try {
          this.isGeoMapLoading = true
          const geoData = await advertiserAPI.getGeoData(this.currentAdvertiserId)

          if (geoData && typeof geoData !== 'string') {
            this.retailerCoordinates = geoData.merchant_coordinates
            this.retailerRegions = geoData.merchant_regions
            this.errorMessage = null
            this.$emit('setRegionsForFilter', geoData.merchant_regions)
          } else {
            this.retailerCoordinates = null
            this.retailerRegions = null
            this.errorMessage = `${errorText}, please refresh or contact support`
          }
        } catch (error) {
          this.retailerCoordinates = null
          this.retailerRegions = null
          this.errorMessage = `${errorText}: ${error}`
        } finally {
          this.setMapDimensions()
        }
      },

      scaleMap() {
        let scaleData
        if (this.retailerRegions && this.retailerRegions.length === 1) {
          scaleData = this.findSingleRegion(this.retailerRegions[0])
          this.mapData = scaleData
          this.projection = d3.geoMercator()
        } else {
          scaleData = usGeoData
          this.mapData = this.usStates
          this.projection = d3.geoAlbersUsa()
        }
        this.projection.fitSize([this.width, this.height], scaleData)
        this.mapPath = d3.geoPath().projection(this.projection)
        this.buildGeoPoints()
      },

      selectLocation(merchantId) {
        let selectedIds
        if (this.selectedRetailerIds.includes(merchantId)) {
          selectedIds = this.selectedRetailerIds.filter(id => id !== merchantId)
        } else {
          selectedIds = [...this.selectedRetailerIds, merchantId]
        }

        this.$emit('setSelectedRetailerIds', {}, selectedIds)
      },

      isLocationSelected(merchantId) {
        return this.selectedRetailerIds?.includes(merchantId)
      },

      setMapDimensions() {
        const $map = this.$refs.map
        let width = $map ? $map.offsetWidth : 250,
          height = width * 0.7
        height = height > 580 ? 580 : height
        this.width = width
        this.height = height
        this.scaleMap()
      },
    },

    created() {
      this.usStates = usGeoData.features
      this.loadRetailersGeoData()
    },

    mounted() {
      window.addEventListener('resize', this.setMapDimensions)
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.setMapDimensions)
    },

    computed: {
      tallerHeight() {
        return this.height + this.margin
      }
    },
  }
</script>

<style lang="sass" scoped>
@import '../../assets/sass/colors'
//
//  retailers map
//
.retailers-map-section
  flex: 1
  max-width: 40%
  @media only screen and (max-width: 40em)
    max-width: 100%
    width: 100%

  circle
    cursor: pointer
    fill: $primary-color
    &.selected
      fill: $negative-hover

  .region-paths path
    fill: white
    stroke: black

  .error-label
    color: $negative-color
</style>